// absolute imports
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import PropTypes from 'prop-types';

// relative imports
import { useAccount } from '../providers/AccountProvider';
import { useLedgibleAuth } from '../providers/LedgibleAuthProvider';
import { useGlobalError } from '../providers/GlobalErrorProvider';
import { applyContext } from './sentry';
import DefaultErrorFallback from './fallback-components/DefaultErrorFallback';
import ModalErrorFallback from './fallback-components/ModalErrorFallback';
import PageErrorFallback from './fallback-components/PageErrorFallback';
import RowErrorFallback from './fallback-components/RowErrorFallback';
import TableErrorFallback from './fallback-components/TableErrorFallback';

const getFallbackComponent = (fallbackType) => {
  switch (fallbackType) {
    // render an error modal display with passed error details
    case 'modal':
      return ModalErrorFallback;
    // render placeholder page for top level errors within pages
    case 'page':
      return PageErrorFallback;
    // render an error table row
    case 'row':
      return RowErrorFallback;
    // render an error display for a table component
    case 'table':
      return TableErrorFallback;
    default:
      return DefaultErrorFallback;
  }
};

const ErrorBoundary = ({
  children,
  fallbackType,
  fallbackProps,
  onReset,
  errorMsg,
  context,
}) => {
  const { user } = useLedgibleAuth() || {};
  const { currentAccount } = useAccount() || {};
  const { notifyChunkLoadFailure } = useGlobalError();
  const Fallback = getFallbackComponent(fallbackType);

  return (
    <SentryErrorBoundary
      beforeCapture={(scope) => {
        scope.setUser({
          id: user?.id || 'unavailable',
        });
        scope.setTransactionName(currentAccount?.applicationType?.id || 'unavailable');
        scope.setTag('accountId', currentAccount?.id || 'unavailable');
        scope.setTag('pathname', window.location.pathname);
        applyContext(scope, context);
      }}
      fallback={({ error, resetError }) => {
        const isChunkLoadError =
          error &&
          typeof error.message === 'string' &&
          error.message.includes('Failed to fetch dynamically imported module');

        if (isChunkLoadError) {
          notifyChunkLoadFailure();
        }

        return (
          <Fallback
            errorMsg={errorMsg}
            resetError={() => {
              resetError();
              if (onReset) {
                onReset();
              }
            }}
            fallbackProps={fallbackProps}
          />
        );
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
};

export default ErrorBoundary;

ErrorBoundary.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  fallbackType: PropTypes.oneOf(['modal', 'page', 'row', 'table', 'default']),
  fallbackProps: PropTypes.shape(),
  errorMsg: PropTypes.string,
  context: PropTypes.shape(),
  onReset: PropTypes.func,
};

ErrorBoundary.defaultProps = {
  fallbackProps: {},
  fallbackType: 'default',
  errorMsg: undefined,
  context: {},
  onReset: undefined,
};
